import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { Link } from "@quintype/components";
import { object, number } from "prop-types";

import { STATIC_KEYWORDS, TRENDING_STORY_FIELDS } from "../../../../constants";
import { getCollectionitems } from "../../../../../api/utils";
import { Span } from "../../../atoms";

import { getCategoryAndSubCategory, getTemplateName, useTrackWidgetImpression } from "../../../../ga-utils";

import "./LatestStories.m.css";

const TrendingStoriesCard = ({ trendingStory, story, config, serialNumber }) => {
  const { headline, slug, ['hero-image-s3-key']: heroImageS3Key } = trendingStory?.story;
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));

  const trackEvent = () => {
    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);
    const templateName = getTemplateName(story?.["story-template"] || pageType);

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "trending_story_click",
        link_position: serialNumber,
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Link href={`/${slug}`} disableAjaxLinks={true} onMouseDown={trackEvent} onTouchStart={trackEvent}>
      <div styleName="story-card">
        <div styleName="story-card-img">
          <div styleName="story-card-img-wrap"><img src={`https://media.fortuneindia.com/${heroImageS3Key}?w=248&auto=format,compress&q=80`} title={headline} alt={headline} width="360" height="220" /></div>
        </div>
        <Span>{headline}</Span>
      </div>
    </Link>
  );
};

TrendingStoriesCard.propTypes = {
  trendingStory: object,
  story: object,
  config: object,
  serialNumber: number,
};

const LatestStories = ({ story, qtConfig }) => {
  const [trendingStories, setTrendingStories] = useState([]);
  const [isInView, setIsInView] = useState(false);
  const { LETEST_STORIES } = STATIC_KEYWORDS;
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const ref = useRef();

  // Intersection Observer hook
  useEffect(() => {
    const observer = new window.IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Load trending stories on first viewport entry and when story.id changes
  useEffect(() => {
    if (!isInView) return;

    getCollectionitems("trending-stories", 0, 3, TRENDING_STORY_FIELDS).then((trendingStoriesResult) => {
      let filteredStories = trendingStoriesResult?.filter((item) => item.id !== story.id);
      if (filteredStories.length > 3) {
        filteredStories = filteredStories?.splice(0, 2);
      }
      setTrendingStories(filteredStories);
    });
  }, [isInView, story.id]);

  const isBrowser = typeof window !== "undefined";

  const trackingOptions = {
    sectionName: "Trending Stories",
    pageType: pageType,
    location: isBrowser ? window.location : null,
    config: qtConfig || qtState.config,
    story: story,
    threshold: 0.2,
  };

  const containerRef = useTrackWidgetImpression(trackingOptions, {
    disabled: !(isBrowser && story && trendingStories.length > 0),
  });

  // Attach observer ref to container so we trigger when visible
  return (
    <div ref={ref} styleName="latest-stories-reserved">
      {trendingStories.length > 0 && (
        <div styleName="trending-stories-container" ref={containerRef}>
          <div styleName="trending-stories-heading">{LETEST_STORIES}</div>
          <div styleName="story-card-wrapper">
            {trendingStories?.map((trendingStory, index) => (
              <TrendingStoriesCard
                trendingStory={trendingStory}
                story={story}
                config={qtConfig}
                key={trendingStory.id}
                serialNumber={index + 1}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

LatestStories.propTypes = {
  story: object.isRequired,
  qtConfig: object,
};

export default LatestStories;
